import React, { useEffect, useMemo, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getTrendColor } from './utils';

/**
 * TrendSlopeOverlay
 * Renders premium visualization for DEE Trend Slope indicator.
 * Displays a glassy floating card with real-time calculations.
 */
const TrendSlopeOverlay = ({ 
    activeIndicators, 
    syncService,
    chartContainerRef 
}) => {
    // Optimization: Local state for high-frequency updates (prevents parent SmartChart re-render)
    const [crosshairData, setCrosshairData] = React.useState(null);

    // Subscribe to SyncService directly
    useEffect(() => {
        if (!syncService) return;
        const unsubscribe = syncService.subscribe((data) => {
            // Only update if we have relevant indicators to avoid wasted renders
            // We can optimize further by checking if data has changed significantly
            setCrosshairData(data);
        });
        return () => unsubscribe();
    }, [syncService]);

    const slopeIndicators = useMemo(() => {
        return (activeIndicators || []).filter(ind => ind.type === 'dee_trend_slope' && !ind.isHidden);
    }, [activeIndicators]);

    if (slopeIndicators.length === 0 || !crosshairData) return null;

    // Auto-compact if more than 2 indicators
    const isGlobalCompact = slopeIndicators.length > 2;

    return createPortal(
        <div 
            className={`absolute top-16 left-4 z-[100] pointer-events-none flex flex-col items-start ${isGlobalCompact ? 'gap-2' : 'gap-3'}`}
            style={{ fontFamily: "'Inter', sans-serif" }}
        >
            <AnimatePresence>
                {slopeIndicators.map((ind, index) => (
                    <SlopeCard 
                        key={ind.id}
                        indicator={ind}
                        data={crosshairData[ind.id]}
                        index={index}
                        forceCompact={isGlobalCompact}
                    />
                ))}
            </AnimatePresence>
        </div>,
        chartContainerRef.current
    );
};

/**
 * SlopeCard Component
 * Renders a single trend slope card (Classic or Premium)
 */
const SlopeCard = memo(({ indicator, data, index, forceCompact }) => {
    if (!data || typeof data !== 'object') return null;

    const { 
        trend = 'neutral', 
        slope = 0, 
        angle = 0, 
        strength = 0, 
        distance = 0, 
        ma = 0, 
        marker = '' 
    } = data;

    // Use visualMode from settings (default to premium for wow factor)
    const visualMode = (indicator.styleOptions?.visualMode || 'premium').toLowerCase();
    const isCompact = forceCompact || indicator.styleOptions?.compactView;
    const markerSettings = indicator.styleOptions?.markerSettings || {};

    // Determine colors based on trend
    const trendColor = getTrendColor(trend);

    return (
        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ delay: index * (isCompact ? 0.05 : 0.1) }}
            className={`
                bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl
                overflow-hidden relative transition-all duration-300
                ${isCompact ? 'p-1.5 px-2.5 min-w-[180px]' : 'p-4 min-w-[220px]'}
                ${visualMode === 'classic' && !isCompact ? 'p-3 min-w-[180px]' : ''}
            `}
        >
            {/* Background Pulse Glow (Premium only) */}
            {visualMode === 'premium' && !isCompact && (
                <div 
                    className="absolute inset-0 opacity-10 transition-colors duration-500" 
                    style={{ background: `radial-gradient(circle at center, ${trendColor}, transparent)` }}
                />
            )}

            {isCompact ? (
                /* REFINED COMPACT VIEW: 2-Line Horizontal Layout */
                <div className="flex items-center gap-3 relative z-10">
                    <div className="flex flex-col flex-1 min-w-0">
                        {/* Line 1: Header (Label + Trend) */}
                        <div className="flex items-center gap-1.5 mb-1">
                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: trendColor, boxShadow: `0 0 6px ${trendColor}` }} />
                            <span className="text-[9px] font-black text-white/90 truncate uppercase tracking-tight">
                                {indicator.label || 'Trend'}
                            </span>
                            <span className="text-[8px] font-bold uppercase ml-auto whitespace-nowrap" style={{ color: trendColor }}>
                                {trend}
                            </span>
                        </div>
                        {/* Line 2: Stats Row */}
                        <div className="flex items-center gap-3 opacity-90">
                            <div className="flex flex-col w-[36px] items-center shrink-0">
                                <span className="text-[10px] font-mono font-bold text-white leading-none">
                                    {angle > 0 ? '+' : ''}{Number(angle).toFixed(0)}°
                                </span>
                            </div>
                            <div className="flex items-center gap-2 border-l border-white/10 pl-2 w-[55px] shrink-0">
                                <span className="text-[7px] font-mono text-white/40 uppercase">S</span>
                                <span className="text-[9px] font-mono text-white/80">
                                    {Number(slope).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 border-l border-white/10 pl-2 min-w-[60px] shrink-0">
                                <span className="text-[7px] font-mono text-white/40 uppercase shrink-0">STR</span>
                                <span className="text-[9px] font-mono font-bold" style={{ color: trendColor }}>
                                    {Number(strength).toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Right: Mini Gauge (Premium Compact) */}
                    {visualMode === 'premium' && (
                        <div className="w-8 h-8 shrink-0 relative flex items-center justify-center opacity-80" style={{ transform: markerSettings.size ? `scale(${markerSettings.size * 0.5})` : undefined }}>
                            <svg viewBox="0 0 100 60" className="w-full h-full overflow-visible">
                                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="10" strokeLinecap="round" />
                                <motion.path 
                                    initial={{ pathLength: 0 }}
                                    animate={{ pathLength: Math.min(Math.abs(angle) / 90, 1), stroke: trendColor }}
                                    d={angle >= 0 ? "M 50 10 A 40 40 0 0 1 90 50" : "M 50 10 A 40 40 0 0 0 10 50"}
                                    fill="none" strokeWidth="10" strokeLinecap="round"
                                />
                                <motion.g animate={{ rotate: angle }} style={{ originX: '50px', originY: '50px' }} transition={{ type: "spring", stiffness: 30, damping: 10 }}>
                                    <line x1="50" y1="50" x2="50" y2="10" stroke="white" strokeWidth="5" strokeLinecap="round" className="drop-shadow-sm" />
                                    <circle cx="50" cy="50" r="5" fill="white" />
                                </motion.g>
                            </svg>
                        </div>
                    )}
                </div>
            ) : (
                /* FULL VIEW: Original Premium or Classic Layout */
                <>
                    <div className={`flex items-center justify-between relative z-10 ${visualMode === 'premium' && !isCompact ? 'mb-4' : 'mb-2'}`}>
                        <div className="flex flex-col">
                            <span className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-0.5">
                                {visualMode === 'premium' ? 'Trend Analysis' : 'Trend Slope'}
                            </span>
                            <span className="text-[9px] text-white/50 uppercase tracking-tight font-bold truncate max-w-[100px]">
                                {indicator.label || 'Indicator'}
                            </span>
                        </div>
                        <div className="flex flex-col items-end">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold uppercase" style={{ color: trendColor }}>{trend}</span>
                                <div 
                                    className="w-2 h-2 rounded-full shadow-[0_0_8px_rgba(0,0,0,0.5)]" 
                                    style={{ 
                                        backgroundColor: trendColor,
                                        boxShadow: `0 0 12px ${trendColor}`,
                                        transform: markerSettings.size ? `scale(${markerSettings.size * 0.5})` : undefined
                                    }}
                                />
                            </div>
                        </div>
                    </div>

                    {visualMode === 'premium' ? (
                        <>
                            <div className="flex justify-center relative z-10 mb-6">
                                <div className="relative w-32 h-16 group" style={{ transform: markerSettings.size ? `scale(${markerSettings.size * 0.7})` : undefined }}>
                                    <svg viewBox="0 0 100 50" className="w-full h-full overflow-visible">
                                        <path d="M 10 45 A 40 40 0 0 1 90 45" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="6" strokeLinecap="round" />
                                        <motion.path 
                                            initial={{ pathLength: 0 }}
                                            animate={{ pathLength: Math.min(Math.abs(angle) / 90, 1), stroke: trendColor }}
                                            transition={{ type: "spring", stiffness: 50 }}
                                            d={angle >= 0 ? "M 50 5 A 40 40 0 0 1 90 45" : "M 50 5 A 40 40 0 0 0 10 45"}
                                            fill="none" strokeWidth="6" strokeLinecap="round" opacity={0.3}
                                        />
                                        <circle cx="50" cy="45" r="2.5" fill="white" />
                                        <motion.g animate={{ rotate: angle }} style={{ originX: '50px', originY: '45px' }} transition={{ type: "spring", stiffness: 30, damping: 10 }}>
                                            <line x1="50" y1="45" x2="50" y2="10" stroke="white" strokeWidth="2" strokeLinecap="round" className="drop-shadow-lg" />
                                            <path d="M 47 10 L 53 10 L 50 5 Z" fill="white" />
                                        </motion.g>
                                        <text x="5" y="48" fontSize="6" fill="white" opacity="0.2" textAnchor="start font-mono">-90°</text>
                                        <text x="50" y="3" fontSize="6" fill="white" opacity="0.2" textAnchor="middle font-mono">0°</text>
                                        <text x="95" y="48" fontSize="6" fill="white" opacity="0.2" textAnchor="end font-mono">+90°</text>
                                    </svg>
                                    <div className="absolute inset-x-0 bottom-0 text-center translate-y-1">
                                        <span className="text-xl font-black font-mono tracking-tighter leading-none" style={{ color: 'white' }}>
                                            {angle > 0 ? '+' : ''}{Number(angle).toFixed(1)}°
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-y-2 pt-1 relative z-10 border-t border-white/5">
                                <StatItem label="Slope" value={slope} precision={3} size="sm" />
                                <StatItem label="Strength" value={strength} precision={3} color={trendColor} align="center" size="sm" />
                                <StatItem label="Shift" value={distance} precision={2} opacity={0.7} align="end" size="sm" />
                            </div>
                        </>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 relative z-10">
                            <StatItem label="MA" value={ma} precision={2} size="sm" />
                            <StatItem label="Angle" value={angle} precision={2} suffix="°" color={trendColor} size="sm" />
                            <StatItem label="Slope" value={slope} precision={3} size="sm" />
                            <StatItem label="Strength" value={strength} precision={3} size="sm" />
                            <StatItem label="Distance" value={distance} precision={2} opacity={0.7} size="sm" />
                        </div>
                    )}

                    {marker && (
                        <div className={`${visualMode === 'premium' ? 'mt-4' : 'mt-2'} px-2 py-1 bg-white/5 rounded-md border border-white/5 flex items-center gap-2 group/marker relative z-10`}>
                            <div className="w-1.5 h-1.5 rounded-full shadow-[0_0_4px_rgba(0,0,0,0.5)]" style={{ backgroundColor: trendColor }} />
                            <span className="text-[10px] text-white/50 font-medium italic truncate flex-1">{marker}</span>
                        </div>
                    )}
                </>
            )}
        </motion.div>
    );
});

const StatItem = memo(({ label, value, precision = 2, suffix = '', color, opacity = 0.9, align = 'start', size = 'sm' }) => (
    <div className={`flex flex-col ${align === 'center' ? 'items-center' : align === 'end' ? 'items-end' : ''}`}>
        <span className={`${size === 'xs' ? 'text-[6px]' : 'text-[8px]'} text-white/30 uppercase font-bold tracking-widest mb-0.5`}>{label}</span>
        <span 
            className={`${size === 'xs' ? 'text-[10px]' : 'text-xs'} font-mono tracking-tighter`} 
            style={{ 
                color: color || `rgba(255,255,255,${opacity})`
            }}
        >
            {value > 0 && suffix === '°' ? '+' : ''}{Number(value).toFixed(precision)}{suffix}
        </span>
    </div>
));

export default memo(TrendSlopeOverlay);
